import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Footer from '@/components/Footer';
import { useAuth, getAuthHeaders } from '@/contexts/AuthContext';
import { ArrowLeft, Clock, BarChart3, CheckCircle2, Users, BookOpen, Zap } from 'lucide-react';

export default function CourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [enrollForm, setEnrollForm] = useState({ phone: '', message: '' });
  const [enrollStatus, setEnrollStatus] = useState('');
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    axios.get(`${API}/courses/${id}`).then(r => setCourse(r.data)).catch(() => navigate('/')).finally(() => setLoading(false));
  }, [id, API, navigate]);

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    setEnrollStatus('sending');
    try {
      await axios.post(`${API}/enrollments`, { course_id: id, phone: enrollForm.phone, message: enrollForm.message },
        { headers: getAuthHeaders(), withCredentials: true });
      setEnrollStatus('success');
      setTimeout(() => { setEnrollDialog(false); setEnrollStatus(''); }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setEnrollStatus(typeof detail === 'string' ? detail : 'Failed to enroll');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FFFEF9]"><div className="w-8 h-8 border-2 border-[#0891B2] border-t-transparent rounded-full animate-spin" /></div>;
  if (!course) return null;

  return (
    <div className="min-h-screen bg-[#FFFEF9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#0891B2] mb-6 transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Programs
        </button>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <span className="level-badge text-xs mb-3 inline-block">{course.level}</span>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#1a1a2e] mb-4" style={{ fontFamily: 'Outfit' }} data-testid="course-detail-title">{course.title}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-6">
                <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-[#0891B2]" /> {course.duration}</span>
                <span className="flex items-center gap-1"><BarChart3 className="w-4 h-4 text-[#0891B2]" /> {course.level}</span>
                <span className="flex items-center gap-1"><BookOpen className="w-4 h-4 text-[#0891B2]" /> {course.category}</span>
              </div>
              {course.image_url && (
                <img src={course.image_url} alt={course.title} className="w-full rounded-2xl border-2 border-[#1a1a2e] max-h-[350px] object-cover mb-8" />
              )}
              <h2 className="text-xl font-bold text-[#1a1a2e] mb-3" style={{ fontFamily: 'Outfit' }}>About This Course</h2>
              <p className="text-gray-600 leading-relaxed" data-testid="course-detail-description">{course.description}</p>
            </motion.div>

            {course.features?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <h2 className="text-xl font-bold text-[#1a1a2e] mb-4" style={{ fontFamily: 'Outfit' }}>What You'll Learn</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {course.features.map((f, i) => (
                    <div key={i} className="neo-card-static flex items-center gap-3 p-4" data-testid={`course-feature-${i}`}>
                      <CheckCircle2 className="w-5 h-5 text-[#0891B2] flex-shrink-0" />
                      <span className="text-sm text-gray-700">{f}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          <div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="neo-card-static p-6 sticky top-24 space-y-6" data-testid="course-sidebar">
              <div className="space-y-4">
                {[
                  { icon: Clock, label: 'Duration', value: course.duration },
                  { icon: BarChart3, label: 'Level', value: course.level },
                  { icon: BookOpen, label: 'Category', value: course.category },
                  { icon: Users, label: 'Mode', value: 'Hands-on + Online' },
                ].map((item, i) => { const Icon = item.icon; return (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-10 h-10 rounded-lg bg-cyan-50 border border-cyan-200 flex items-center justify-center"><Icon className="w-5 h-5 text-[#0891B2]" /></div>
                    <div><div className="text-xs text-gray-400">{item.label}</div><div className="text-[#1a1a2e] font-medium">{item.value}</div></div>
                  </div>
                ); })}
              </div>
              <div className="border-t border-gray-200 pt-5">
                <button onClick={() => user ? setEnrollDialog(true) : navigate('/login')}
                  className="btn-cyan w-full flex items-center justify-center gap-2 text-sm !rounded-xl" data-testid="course-enroll-btn">
                  <Zap className="w-4 h-4" /> ENROLL NOW
                </button>
                <p className="text-xs text-gray-400 text-center mt-3">Free enrollment. Start learning today.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Dialog open={enrollDialog} onOpenChange={setEnrollDialog}>
        <DialogContent className="bg-white border-gray-200 text-[#1a1a2e] max-w-md" data-testid="enroll-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Enroll in {course.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEnroll} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Phone Number</label>
              <Input value={enrollForm.phone} onChange={e => setEnrollForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91 99999 99999"
                className="bg-[#FEFCE8] border-gray-200 focus:border-[#0891B2]" data-testid="enroll-phone-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Message (optional)</label>
              <Textarea value={enrollForm.message} onChange={e => setEnrollForm(p => ({ ...p, message: e.target.value }))} placeholder="Any questions..." rows={3}
                className="bg-[#FEFCE8] border-gray-200 focus:border-[#0891B2] resize-none" data-testid="enroll-message-input" />
            </div>
            <button type="submit" disabled={enrollStatus === 'sending'}
              className="btn-cyan w-full text-sm !rounded-xl" data-testid="enroll-submit-btn">
              {enrollStatus === 'sending' ? 'Enrolling...' : 'CONFIRM ENROLLMENT'}
            </button>
            {enrollStatus === 'success' && <p className="text-sm text-green-600 text-center" data-testid="enroll-success-msg">Enrolled successfully!</p>}
            {enrollStatus && enrollStatus !== 'sending' && enrollStatus !== 'success' && <p className="text-sm text-red-500 text-center" data-testid="enroll-error-msg">{enrollStatus}</p>}
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
